import {
    AlertBannerType,
    AlertBannerVariant,
    ButtonFlavor,
    ButtonType,
    ExAlertBanner,
    ExButton,
    ExEmptyState,
    ExInput,
    ExLink,
    ExLoader,
    LinkSize,
} from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FABRIC_BASE_URL } from '../config';
import ConnectionDrawer from '../connections/ConnectionDrawer';
import { type Connection, getAllConnections } from '../connections/connections';
import { useConnectionDrawer } from '../connections/useConnectionDrawer';
import {
    type AuthorizationStrategy,
    type Connector,
    getConnectors,
} from '../connectors/connectors';
import { fetchIdentityProviders } from '../identity-providers/identityProviders';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';
import ErrorAlertBanner from '../shared/ErrorAlertBanner';
import FilterSelect from '../shared/FilterSelect';
import NoSearchResults from '../shared/NoSearchResults';
import styles from './Home.module.css';
import HomeCard from './HomeCard';

const RECENT_LIMIT = 8;

const AUTHORIZATION_STRATEGY_LABELS: Record<AuthorizationStrategy, string> = {
    oAuth2AuthorizationCode: 'OAuth 2.0 Authorization Code',
    oAuth2ClientCredential: 'OAuth 2.0 Client Credentials',
    apiKey: 'API Key',
    basic: 'Basic Auth',
    bearerJwt: 'Bearer JWT',
};

const Home = () => {
    const navigate = useNavigate();
    const [connections, setConnections] = useState<Connection[]>([]);
    const [enabledIdsToApps, setEnabledIdsToApps] = useState<Record<string, string[]>>({});
    const [connectors, setConnectors] = useState<Connector[]>([]);
    const [showIDPSetup, setShowIDPSetup] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [search, setSearch] = useState('');
    const [selectedStatus, setSelectedStatus] = useState<Set<'Configured' | 'Not Configured'>>(
        new Set(),
    );
    const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());

    // biome-ignore lint/correctness/useExhaustiveDependencies: Refresh the key when refreshKey changes
    useEffect(() => {
        setLoading(true);
        setError(null);
        Promise.all([getAllConnections(), getConnectors(), fetchIdentityProviders()])
            .then(([connectionsData, connectorsData, identityProvidersData]) => {
                const sorted = [...connectionsData].sort(
                    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
                );
                setConnections(sorted.slice(0, RECENT_LIMIT));

                const enabledIdsToApps: Record<string, string[]> = {};
                for (const connection of connectionsData.filter((c) => c.enabled)) {
                    if (!enabledIdsToApps[connection.connectorId]) {
                        enabledIdsToApps[connection.connectorId] = [];
                    }
                    enabledIdsToApps[connection.connectorId].push(connection.appId);
                }
                setEnabledIdsToApps(enabledIdsToApps);
                setConnectors(connectorsData.sort((a, b) => a.name.localeCompare(b.name)));

                setShowIDPSetup(identityProvidersData.results.length === 0);
            })
            .catch((err) => setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE))
            .finally(() => setLoading(false));
    }, [refreshKey]);

    const connectorMap = new Map(connectors.map((c) => [c.id, c]));

    const availableTypes = [
        ...new Set(connectors.map((c) => AUTHORIZATION_STRATEGY_LABELS[c.authorizationStrategy])),
    ].sort();

    const filteredConnectors = connectors.filter((c) => {
        if (
            // filter by search
            (search && !c.name.toLowerCase().includes(search.toLowerCase())) ||
            // filter by status
            (selectedStatus.size &&
                !selectedStatus.has(enabledIdsToApps[c.id] ? 'Configured' : 'Not Configured')) ||
            // filter by auth type
            (selectedTypes.size &&
                !selectedTypes.has(AUTHORIZATION_STRATEGY_LABELS[c.authorizationStrategy]))
        ) {
            return false;
        }
        return true;
    });

    const {
        drawerMode,
        openCreate,
        openConnection,
        openConnectorView,
        onConnectorSelect,
        navigateToBase,
    } = useConnectionDrawer('home', connections, { create: 'connection/new', edit: 'connection' });

    return (
        <section className={styles.homeWrapper}>
            {showIDPSetup && (
                <ExAlertBanner
                    className={styles.idpSetupAlert}
                    type={AlertBannerType.ERROR}
                    variant={AlertBannerVariant.SUBTLE}
                    open
                >
                    <div className={styles.idpSetupBody}>
                        <strong>Set IDP</strong>
                        <span className={styles.idpSetupText}>
                            One quick step: connect your identity provider to unlock all 1,100+
                            connectors.
                        </span>

                        <ExLink
                            data-pendo-link="home-setidp"
                            onClick={() => navigate('/identity-providers/new')}
                            trailingIcon={'Right arrow'}
                            size={LinkSize.SMALL}
                        >
                            Set IDP
                        </ExLink>
                    </div>
                </ExAlertBanner>
            )}

            <section className={styles.sectionBox}>
                <div className={styles.header}>
                    <h2 className={styles.recentHeading}>Recent Configs</h2>
                    {connectors.length > 0 && !error && (
                        <div className={styles.actions}>
                            {connections.length > 0 && (
                                <ExButton
                                    data-pendo-button="home-recentconfigs-viewall"
                                    type={ButtonType.SECONDARY}
                                    flavor={ButtonFlavor.BRANDED}
                                    onClick={() => navigate('/connections')}
                                >
                                    View All
                                </ExButton>
                            )}
                            <ExButton
                                data-pendo-button="home-recentconfigs-createconfig"
                                type={ButtonType.PRIMARY}
                                flavor={ButtonFlavor.BRANDED}
                                onClick={openCreate}
                            >
                                Create Config
                            </ExButton>
                        </div>
                    )}
                </div>

                {error ? (
                    <ErrorAlertBanner onClose={() => setError(null)}>{error}</ErrorAlertBanner>
                ) : connections.length === 0 ? (
                    loading ? (
                        <ExLoader label="Loading recent configs…" />
                    ) : (
                        <ExEmptyState
                            className={styles.noResults}
                            label="No configs configured"
                            text="Create a config to get started."
                        >
                            <div slot="header">
                                <img
                                    src={`${FABRIC_BASE_URL}/emptyStateIcon.svg`}
                                    alt="No configs configured"
                                />
                            </div>
                        </ExEmptyState>
                    )
                ) : (
                    <div className={styles.cardRow}>
                        {connections.map((connection) => (
                            <div key={connection.appId} className={styles.cardWrap}>
                                <HomeCard
                                    data-pendo-card="home-recentconfigs-config"
                                    data-pendo-connectorid={connection.connectorId}
                                    data-pendo-connectionid={connection.appId}
                                    name={connection.name}
                                    showCheck={connection.enabled}
                                    subtext={connection.identityProviderName}
                                    iconSrc={connectorMap.get(connection.connectorId)?.icon}
                                    onSelect={() => openConnection(connection.appId)}
                                    kind="config"
                                />
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <ConnectionDrawer
                mode={drawerMode}
                onConnectorView={openConnectorView}
                onConnectorSelect={onConnectorSelect}
                onConnectionSelect={openConnection}
                onBack={openCreate}
                onClose={() => {
                    setRefreshKey((k) => k + 1);
                    navigateToBase();
                }}
            />

            <section className={styles.sectionBox}>
                <h2 className={styles.allConnectorsHeading}>All Connectors</h2>
                <div className={styles.filterRow}>
                    <ExInput
                        data-pendo-input="home-allconnectors-search"
                        type="search"
                        className={styles.search}
                        placeholder="Search connectors"
                        leadingIcon="magnifying-glass"
                        value={search}
                        onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
                        clearable
                    />
                    <FilterSelect
                        placeholder="Status"
                        options={['Configured', 'Not Configured']}
                        value={selectedStatus}
                        onChange={setSelectedStatus}
                    />
                    <FilterSelect
                        placeholder="Type"
                        options={availableTypes}
                        value={selectedTypes}
                        onChange={setSelectedTypes}
                    />
                </div>

                {filteredConnectors.length === 0 ? (
                    loading ? (
                        <ExLoader label="Loading connectors…" />
                    ) : (
                        <NoSearchResults itemLabel="connectors" search={search} />
                    )
                ) : (
                    <div className={styles.allConnectorsCardRow}>
                        {filteredConnectors.map((connector) => (
                            <HomeCard
                                key={connector.id}
                                data-pendo-card="home-allconnectors-connector"
                                data-pendo-connectorid={connector.id}
                                name={connector.name}
                                showCheck={Object.keys(enabledIdsToApps).includes(connector.id)}
                                iconSrc={connector.icon}
                                onSelect={() => openConnectorView(connector.id)}
                            />
                        ))}
                    </div>
                )}
            </section>
        </section>
    );
};

export default Home;
